import React, { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { Link, useSearchParams } from "react-router-dom";
import { CHECK_OTP, LOGIN } from "../../../queries/auth";
import { get } from "lodash";

type TLoginEmailInputs = {
  username: string;
  password: string;
};

type TOTPInputs = {
  id: string;
  otp: string;
};

export const LoginForm = ({ onSuccess }: any) => {
  const { check } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const { register: loginRegister, handleSubmit: loginHandleSubmit } =
    useForm<TLoginEmailInputs>({
      defaultValues: {
        username: searchParams.get("email") as string,
      },
    });

  const { register: otpRegister, handleSubmit: otpHandleSubmit } =
    useForm<TOTPInputs>();

  const [state, setState] = useState({
    sessionID: null,
    otp_expire: null,
    first_name: null,
    error: null,
  });

  const [login, { loading }] = useMutation(LOGIN);

  const [checkOTP, { error: OTPError, loading: OTPLoading }] =
    useMutation(CHECK_OTP);

  const onLoginSubmit = (data: any) => {
    login({ variables: { ...data } })
      .then((r) => {
        if (get(r, "data.login.status", 0) == 1) {
          check().then((res: any) => {
            onSuccess(res);
          });
        }
        setState({
          sessionID: r.data.login.id,
          first_name: r.data.login.user.first_name,
          otp_expire: r.data.login.otp_expire_at,
          error: null,
        });
      })
      .catch((e: any) => {
        setState({
          ...state,
          error: e.message,
        });
      });
  };

  const onOTPSubmit = (data: any) => {
    checkOTP({ variables: { ...data } }).then((r) => {
      check().then((res: any) => {
        onSuccess(res);
      });
    });
  };

  if (state.sessionID !== null && state.first_name !== null) {
    return (
      <div className="w-full pt-4">
        <p>Enter the code we sent to the email associated with your account.</p>
        {OTPError && (
          <p className="bg-red-200 text-red-800 rounded-md py-1 px-2 my-2">
            {OTPError.message}
          </p>
        )}
        <form onSubmit={otpHandleSubmit(onOTPSubmit)}>
          <div className="flex flex-col gap-2">
            <label className="text-ab-grey">OTP (One Time Password)</label>
            <input
              type="text"
              autoComplete="off"
              placeholder="Check your inbox for an OTP"
              {...otpRegister("otp")}
              className="border border-ab-lightdarkgreen rounded-md py-1.5 px-2 focus:outline-none shadow-sm"
              disabled={OTPLoading}
            />
            <input
              type="text"
              autoComplete="off"
              {...otpRegister("id")}
              value={state.sessionID}
              className="hidden"
            />
            <button
              type="submit"
              className="bg-ab-lightdarkgreen py-2 px-2 flex items-center justify-center rounded-md text-ab-lightteal font-semibold w-full"
              disabled={OTPLoading}
            >
              {OTPLoading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-ab-lightteal"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Verifying...
                </>
              ) : (
                "Submit OTP"
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full pt-4">
      {!loading && state.error && (
        <p className="bg-red-200 text-red-800 rounded-md py-1 px-2 my-2">
          {state.error}
        </p>
      )}
      {searchParams.get("email") && (
        <p className="bg-green-200 text-green-800 rounded-md py-1 px-2 my-2">
          You can now sign-in
        </p>
      )}
      <form
        onSubmit={loginHandleSubmit(onLoginSubmit)}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-2">
          <label className="font-semibold">Email</label>
          <input
            type="text"
            autoComplete="off"
            placeholder="Email address"
            {...loginRegister("username")}
            className="border border-ab-lightdarkgreen rounded-md py-1.5 px-2 focus:outline-none shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={loading}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-semibold">Password</label>
          <input
            type="password"
            autoComplete="off"
            placeholder="Password"
            {...loginRegister("password")}
            className="border border-ab-lightdarkgreen rounded-md py-1.5 px-2 focus:outline-none shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={loading}
          />
        </div>
        <div className="flex my-2 justify-between items-center">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              name="rememberMe"
              id="rememberMe"
              className="w-4 h-4 accent-[#015659]"
            />
            <p className="text-sm font-semibold text-[#2E2C34]">Remember Me</p>
          </div>
          <Link to="/password-reset" className="font-semibold text-[#015659]">
            Forgot password?
          </Link>
        </div>
        <button
          type="submit"
          className="bg-[#015659] text-nowrap py-2 px-12 md:px-16 rounded-md text-white font-semibold flex items-center justify-center w-full disabled:opacity-70 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Signing in...
            </>
          ) : (
            "Sign in"
          )}
        </button>
      </form>
    </div>
  );
};
